import React, { useState } from 'react';
import { LocalLink as Link } from '../../components/LocalLink';
import { Instagram, Facebook, Linkedin, Phone, Mail, MapPin } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { productsCatalog } from '../../data/productsCatalog';
import { API_BASE } from '../../config';
import { trackWhatsAppClick, trackEmailClick, trackPhoneClick, trackEvent } from '../../utils/analytics';

export default function Footer() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if ((e.target as any).botcheck?.value) {
      setStatus('success');
      return;
    }
    
    setStatus('loading');
    try {
      const response = await fetch(`${API_BASE}/api/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      const data = await response.json();
      if (data.success) {
        setStatus('success');
        setEmail('');
        trackEvent('newsletter_subscribe', {
          event_category: 'Form Submission',
          event_label: 'Footer Newsletter',
        });
      } else {
        setStatus('error');
      }
    } catch (err) {
      setStatus('error');
    }
    
    setTimeout(() => {
      setStatus('idle');
    }, 5000);
  };

  return (
    <footer className="bg-spring-text text-spring-bg py-16 px-4 md:px-16">
      
      {/* Newsletter Section */}
      <div className="max-w-7xl mx-auto border-b border-spring-bg/20 pb-16 mb-16 flex flex-col md:flex-row items-center justify-between gap-8">
        <div className="max-w-xl text-center md:text-left">
          <h3 className="text-3xl font-display font-medium mb-4">{t('footer.newsletter.title')}</h3>
          <p className="text-spring-bg/70 font-light">
            {t('footer.newsletter.desc')}
          </p>
        </div>
        <form className="w-full md:w-auto flex flex-col sm:flex-row gap-3" onSubmit={handleSubscribe}>
          <input type="text" name="botcheck" className="hidden" style={{ display: 'none' }} tabIndex={-1} autoComplete="off" />
          <input 
            type="email" 
            id="newsletter-email"
            name="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t('footer.newsletter.placeholder')} 
            className="px-6 py-4 bg-spring-bg/5 border border-spring-bg/20 text-spring-bg placeholder:text-spring-bg/50 focus:outline-none focus:border-spring-accent3 transition-colors min-w-[280px]"
            required
          />
          <button 
            type="submit"
            disabled={status === 'loading' || status === 'success'}
            className="px-8 py-4 bg-spring-bg text-spring-text font-medium hover:bg-spring-accent3 transition-colors disabled:opacity-50 min-w-[140px]"
          >
            {status === 'loading' ? '...' : status === 'success' ? '✓' : t('footer.newsletter.subscribe')}
          </button>
        </form>
      </div>

      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 border-b border-spring-bg/20 pb-12 mb-8">
        
        {/* Column 1 - Brand */}
        <div className="space-y-6">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1030.06 150" className="h-8 w-auto" fill="currentColor" data-name="Calque 1">
            <path d="M541.7 37.01h12.65c4.48 0 8.31-.73 11.48-2.2s5.61-3.56 7.31-6.27c1.71-2.71 2.56-5.96 2.56-9.74s-.85-7.04-2.56-9.78c-1.71-2.73-4.15-4.82-7.31-6.27-3.17-1.44-7-2.16-11.48-2.16h-19.97v52.24h7.31V37.01Zm23.08-26.86c2.41 2.04 3.62 4.93 3.62 8.66s-1.21 6.62-3.62 8.66-5.96 3.06-10.64 3.06h-12.43V7.09h12.43c4.68 0 8.23 1.02 10.64 3.06M595.02 29.48h25.66v-6.35h-25.66V7.09h28.81V.6H587.7v52.23h37.15v-6.49h-29.83zM674.65 39.52 643.94.6h-6v52.23h7.32V13.91l30.71 38.92h6V.6h-7.32zM734.58 3.88C730.41 1.69 725.6.6 720.14.6h-21.57v52.24h21.57c5.46 0 10.28-1.09 14.44-3.28q6.255-3.285 9.72-9.18c2.32-3.93 3.47-8.48 3.47-13.66s-1.16-9.73-3.47-13.66-5.56-6.99-9.72-9.18m3.33 33.09q-2.565 4.44-7.2 6.9c-3.1 1.64-6.76 2.46-11.01 2.46h-13.82V7.09h13.82q6.36 0 11.01 2.46c3.09 1.64 5.5 3.93 7.2 6.87 1.71 2.94 2.56 6.37 2.56 10.3s-.85 7.3-2.56 10.26ZM760.22.6h7.31v52.24h-7.31zM791.44 13.91l30.72 38.92h5.99V.6h-7.31v38.92L790.13.6h-6v52.23h7.31zM547.41 108.88c1.8-1.79 3.94-3.18 6.4-4.18 2.46-.99 5.13-1.49 8.01-1.49s5.39.51 7.83 1.53 4.63 2.58 6.58 4.66l4.75-4.7c-2.34-2.69-5.18-4.71-8.52-6.08s-6.99-2.05-10.93-2.05-7.5.66-10.79 1.98-6.16 3.18-8.59 5.6-4.34 5.24-5.7 8.47c-1.37 3.24-2.05 6.79-2.05 10.67s.68 7.44 2.05 10.67c1.36 3.23 3.25 6.06 5.67 8.47 2.41 2.42 5.27 4.28 8.56 5.6s6.89 1.98 10.79 1.98 7.67-.69 11.01-2.09q5.01-2.085 8.52-6.12l-4.75-4.7c-1.95 2.14-4.14 3.72-6.58 4.74q-3.66 1.53-7.83 1.53c-4.17 0-5.54-.5-8.01-1.49-2.46-.99-4.6-2.39-6.4-4.18s-3.21-3.92-4.21-6.38-1.5-5.14-1.5-8.02.5-5.56 1.5-8.02 2.4-4.59 4.21-6.38ZM599.26 97.17h-7.32v52.23h35.03v-6.49h-27.71zM678.24 104.15c-2.44-2.41-5.31-4.28-8.63-5.6s-6.92-1.98-10.82-1.98-7.58.67-10.89 2.01c-3.32 1.34-6.19 3.22-8.63 5.63s-4.34 5.24-5.7 8.47c-1.37 3.23-2.05 6.77-2.05 10.6s.68 7.36 2.05 10.6c1.37 3.23 3.27 6.06 5.7 8.47 2.44 2.41 5.33 4.29 8.67 5.63q5.01 2.01 10.86 2.01c5.85 0 7.51-.66 10.82-1.98 3.32-1.32 6.19-3.18 8.63-5.6 2.44-2.41 4.33-5.23 5.67-8.47 1.34-3.23 2.01-6.79 2.01-10.67s-.67-7.38-2.01-10.63c-1.34-3.26-3.23-6.09-5.67-8.51Zm-1.14 27.12c-1 2.44-2.39 4.57-4.17 6.38-1.78 1.82-3.88 3.22-6.29 4.22s-5.06 1.49-7.93 1.49-5.45-.5-7.86-1.49-4.52-2.4-6.33-4.22c-1.8-1.82-3.21-3.94-4.21-6.38-1-2.43-1.5-5.1-1.5-7.98s.5-5.53 1.5-7.95c1-2.41 2.4-4.54 4.21-6.38 1.8-1.84 3.91-3.26 6.33-4.25 2.41-.99 5.03-1.49 7.86-1.49s5.52.5 7.93 1.49c2.41 1 4.51 2.41 6.29 4.25s3.17 3.97 4.17 6.38q1.5 3.615 1.5 7.95c0 4.335-.5 5.55-1.5 7.98M734.11 126.57c0 5.82-1.26 10.07-3.77 12.76s-6.08 4.03-10.71 4.03-8.13-1.34-10.64-4.03-3.77-6.94-3.77-12.76v-29.4h-7.31v29.7c0 7.56 1.92 13.31 5.78 17.24 3.85 3.93 9.14 5.89 15.87 5.89s12.08-1.96 15.91-5.89 5.74-9.68 5.74-17.24v-29.7h-7.09v29.4ZM793.46 100.45c-4.17-2.19-8.98-3.28-14.44-3.28h-21.57v52.24h21.57c5.46 0 10.28-1.1 14.44-3.28q6.255-3.285 9.72-9.18c2.32-3.93 3.47-8.48 3.47-13.66s-1.16-9.72-3.47-13.66c-2.31-3.93-5.56-6.99-9.72-9.18m3.32 33.1q-2.565 4.44-7.2 6.9c-3.1 1.64-6.76 2.46-11.01 2.46h-13.82v-39.25h13.82q6.36 0 11.01 2.46c3.09 1.64 5.5 3.93 7.2 6.87 1.71 2.93 2.56 6.37 2.56 10.3s-.85 7.3-2.56 10.26M847.71 124.26c-1.83-1.05-3.85-1.89-6.07-2.54s-4.42-1.23-6.62-1.75c-2.19-.52-4.21-1.11-6.03-1.75s-3.32-1.52-4.46-2.61-1.72-2.56-1.72-4.4c0-1.54.43-2.93 1.28-4.18.85-1.24 2.21-2.24 4.06-2.98 1.85-.75 4.22-1.12 7.09-1.12 2.29 0 4.66.34 7.09 1.01 2.44.67 4.75 1.76 6.95 3.25l2.41-5.97c-2.15-1.49-4.68-2.64-7.61-3.43-2.92-.8-5.87-1.19-8.85-1.19-4.49 0-8.18.68-11.08 2.05s-5.05 3.19-6.44 5.45-2.08 4.74-2.08 7.42.56 5.06 1.68 6.83 2.6 3.17 4.42 4.22c1.83 1.04 3.85 1.9 6.07 2.57s4.42 1.26 6.62 1.75c2.19.5 4.21 1.08 6.03 1.75 1.83.67 3.31 1.56 4.46 2.65 1.14 1.1 1.72 2.54 1.72 4.33q0 2.31-1.32 4.14c-.88 1.22-2.24 2.18-4.1 2.87-1.85.7-4.27 1.05-7.24 1.05-3.27 0-6.41-.57-9.43-1.72-3.02-1.14-5.51-2.58-7.46-4.33l-2.7 5.82c2.05 1.94 4.85 3.52 8.41 4.74q5.34 1.83 11.19 1.83c4.48 0 8.2-.68 11.15-2.05s5.13-3.18 6.55-5.45c1.41-2.26 2.12-4.71 2.12-7.35s-.56-4.97-1.68-6.72c-1.12-1.74-2.6-3.13-4.43-4.18ZM902.09 97.17l-23.18 52.24h7.53l5.62-13.06h27.22l5.62 13.06h7.68l-23.26-52.24h-7.24Zm-7.46 33.21 11.04-25.63 11.04 25.63zM972.88 131.34c3.17-1.44 5.61-3.52 7.31-6.23 1.71-2.71 2.56-5.96 2.56-9.74 0-5.72-1.9-10.19-5.7-13.4s-9.02-4.81-15.65-4.81h-19.96v52.24h7.31v-15.9h12.65c1.13 0 2.21-.05 3.25-.14l11.01 16.03h7.97l-12.17-17.52c.48-.18.97-.33 1.42-.54Zm-11.7-4.17h-12.43v-23.51h12.43c4.68 0 8.23 1.02 10.64 3.06s3.62 4.93 3.62 8.66-1.21 6.63-3.62 8.69-5.96 3.1-10.64 3.1M987.72 97.17v6.49h17.55v45.74h7.24v-45.74h17.55v-6.49zM415.99 2.77 315.33 148.76c-.49.71-1.43.96-2.21.6L.61 2.02C-.42 1.54-.07 0 1.06 0h413.47c1.42 0 2.26 1.6 1.45 2.77ZM868.44 31.04h11.78v12.35c-1.32.88-2.69 1.6-4.13 2.1-2.51.87-5.13 1.31-7.86 1.31-2.93 0-5.63-.5-8.12-1.49s-4.64-2.4-6.47-4.22-3.24-3.96-4.24-6.42q-1.5-3.69-1.5-7.95c0-4.26.5-5.56 1.5-8.02s2.41-4.59 4.24-6.38 4-3.18 6.51-4.18c2.51-.99 5.23-1.49 8.15-1.49s5.54.48 8.01 1.46q3.69 1.455 6.84 4.59l4.54-4.63c-2.34-2.69-5.19-4.7-8.56-6.04S872.06.02 868.01.02 860.35.68 857.04 2c-3.32 1.32-6.21 3.18-8.67 5.6-2.46 2.41-4.37 5.24-5.74 8.47-1.37 3.24-2.05 6.79-2.05 10.67s.68 7.44 2.05 10.67 3.27 6.06 5.7 8.47c2.44 2.41 5.31 4.28 8.63 5.6 3.31 1.32 6.95 1.98 10.89 1.98q5.415 0 10.5-1.68c3.39-1.12 6.35-2.77 8.88-4.96V24.1h-18.8v6.97Z"/>
          </svg>
          <p className="text-spring-bg/70 text-sm leading-relaxed">
            {t('footer.brand.desc1')}<br />
            {t('footer.brand.desc1_sub')}
          </p>
          <p className="text-spring-bg/70 text-sm">
            {t('footer.brand.desc2')}
          </p>
        </div>

        {/* Column 2 - Quick Links */}
        <div className="space-y-4">
          <h4 className="text-sm font-semibold tracking-widest uppercase text-spring-accent3">{t('footer.links.title')}</h4>
          <ul className="space-y-2 text-sm text-spring-bg/80">
            <li><Link to="/" className="hover:text-white transition-colors">{t('header.menu.home')}</Link></li>
            <li><Link to="/about" className="hover:text-white transition-colors">{t('header.menu.about')}</Link></li>
            <li><Link to="/works" className="hover:text-white transition-colors">{t('header.menu.factory')}</Link></li>
            <li><Link to="/services" className="hover:text-white transition-colors">{t('header.menu.services')}</Link></li>
            <li><Link to="/projects" className="hover:text-white transition-colors">{t('header.menu.projects')}</Link></li>
            <li><Link to="/exhibitions" className="hover:text-white transition-colors">{t('header.menu.exhibitions', 'Art Exhibitions & Fairs')}</Link></li>
            <li><Link to="/blog" className="hover:text-white transition-colors">{t('header.menu.blog')}</Link></li>
            <li><Link to="/contact" className="hover:text-white transition-colors">{t('header.menu.contact')}</Link></li>
            <li><Link to="/faq" className="hover:text-white transition-colors">{t('header.menu.faq')}</Link></li>
          </ul>
        </div>

        {/* Column 3 - Capabilities */}
        <div className="space-y-4">
          <h4 className="text-sm font-semibold tracking-widest uppercase text-spring-accent3">{t('footer.capabilities.title')}</h4>
          <ul className="space-y-2 text-sm text-spring-bg/80">
            {productsCatalog.filter(category => category.slug !== 'exhibition-artworks').map(category => (
              <li key={category.slug}>
                <Link to={`/catalogue/${category.slug}`} className="hover:text-white transition-colors">
                  {t(`data.categories.${category.slug}.name`, category.name)}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Column 4 - Contact */}
        <div className="space-y-4">
          <h4 className="text-sm font-semibold tracking-widest uppercase text-spring-accent3">{t('footer.contact.title')}</h4>
          <ul className="space-y-3 text-sm text-spring-bg/80">
            <li className="flex items-center gap-3">
              <Phone size={16} className="text-spring-accent3 flex-shrink-0" />
              <div className="flex items-center gap-2 flex-wrap">
                <a 
                  href="tel:+8618943549167" 
                  onClick={() => trackPhoneClick('Footer')}
                  className="hover:text-white transition-colors"
                >
                  +86 18943549167
                </a>
                <span className="text-spring-bg/40">·</span>
                <a 
                  href="https://wa.me/8618943549167" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  onClick={() => trackWhatsAppClick('Footer Contact List')}
                  className="text-spring-accent3 hover:text-white transition-colors underline underline-offset-2"
                >
                  WhatsApp
                </a>
              </div>
            </li>
            <li className="flex items-center gap-3">
              <Mail size={16} className="text-spring-accent3 flex-shrink-0" />
              <a 
                href="mailto:ann@clouds-art.com" 
                onClick={() => trackEmailClick('Footer Contact List', 'ann@clouds-art.com')}
                className="hover:text-white transition-colors"
              >
                ann@clouds-art.com
              </a>
            </li>
            <li className="flex items-start gap-3 pt-1 leading-relaxed">
              <MapPin size={16} className="text-spring-accent3 mt-1 flex-shrink-0" />
              <span>
                {t('footer.contact.address1')}<br />
                {t('footer.contact.address2')}<br />
                {t('footer.contact.address3')}
              </span>
            </li>
            <li className="flex gap-4 pt-4">
              <a href="https://www.instagram.com/ann_artconsulant/" target="_blank" rel="noreferrer" className="hover:text-white transition-colors hover:scale-110 transform duration-300" aria-label="Instagram">
                <Instagram size={20} strokeWidth={1.5} />
              </a>
              <a href="https://www.facebook.com/share/18fnnWD9xn/?mibextid=wwXIfr" target="_blank" rel="noreferrer" className="hover:text-white transition-colors hover:scale-110 transform duration-300" aria-label="Facebook">
                <Facebook size={20} strokeWidth={1.5} />
              </a>
              <a href="https://www.linkedin.com/in/pending-clouds-art-2376673a7/" target="_blank" rel="noreferrer" className="hover:text-white transition-colors hover:scale-110 transform duration-300" aria-label="LinkedIn">
                <Linkedin size={20} strokeWidth={1.5} />
              </a>
            </li>
          </ul>
        </div>

      </div>

      {/* Bottom Bar */}
      <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center text-xs text-spring-bg/80 mt-8 pt-8 border-t border-spring-bg/10">
        <p>{t('footer.bottom.rights').replace('{{year}}', new Date().getFullYear().toString())}</p>
        <div className="flex gap-4 mt-4 md:mt-0 items-center flex-wrap justify-center">
          <Link to="/legal" className="hover:text-white transition-colors underline-offset-4 hover:underline">Mentions Légales (Legal)</Link>
          <span className="hidden md:inline">|</span>
          <p>{t('footer.bottom.slogan')}</p>
        </div>
      </div>
    </footer>
  );
}
